/**
 * sirus/MicroMenu — R232q20 Phase 4.
 *
 * WoW 3.3.5a / Sirus 12-button micro-menu row, anchored 12px above the main
 * ActionBar. Compact 24×24 buttons each toggle a top-level panel: character
 * pane, spell book, talent tree, achievements, quest log, social, PvP, LFG,
 * help, shop, bags, main menu.
 *
 * Toggles persist in a tiny inline `useUiPanels` Zustand store (localStorage
 * key `aiwari-ui-panels`). The store only holds binary flags — actual panel
 * components subscribe and render themselves; this row is purely toggle UI.
 *
 * DEFERRED:
 *   – AchievementsPanel, SocialPanel, PvPPanel, LFGPanel, HelpModal, ShopModal,
 *     full MainMenu — for now those buttons either toggle the flag (if the
 *     panel mounts elsewhere) or fall back to console.log + simple overlay.
 *   – Bind labels overridable through keybinds-store.ts (resolveKey) — only
 *     hot-keys C/P/N/L/B/Esc are wired here; others will follow when the
 *     keybinds-store grows panel actions.
 */

import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { create } from 'zustand';

import { useUI } from '../../state/store.js';

/* ─────────────────────────────────────────────────────────────────
 * Inline UiPanels store (binary flags only)
 * ──────────────────────────────────────────────────────────────── */

const PANELS_KEY = 'aiwari-ui-panels';

export interface UiPanels {
  characterPane: boolean;
  spellBook: boolean;
  talentTree: boolean;
  achievements: boolean;
  questLog: boolean;
  social: boolean;
  pvp: boolean;
  lfg: boolean;
  help: boolean;
  shop: boolean;
  bagsAll: boolean;
  mainMenu: boolean;
}

interface UiPanelsStore extends UiPanels {
  toggle: (key: keyof UiPanels) => void;
  set: <K extends keyof UiPanels>(key: K, value: UiPanels[K]) => void;
  closeAll: () => void;
}

const DEFAULT_PANELS: UiPanels = {
  characterPane: false,
  spellBook: false,
  talentTree: false,
  achievements: false,
  questLog: false,
  social: false,
  pvp: false,
  lfg: false,
  help: false,
  shop: false,
  bagsAll: false,
  mainMenu: false,
};

function loadPanels(): UiPanels {
  try {
    const raw = localStorage.getItem(PANELS_KEY);
    if (!raw) return { ...DEFAULT_PANELS };
    const parsed = JSON.parse(raw) as Partial<UiPanels>;
    const merged = { ...DEFAULT_PANELS };
    for (const k of Object.keys(DEFAULT_PANELS) as (keyof UiPanels)[]) {
      const v = parsed[k];
      if (typeof v === 'boolean') merged[k] = v;
    }
    return merged;
  } catch {
    return { ...DEFAULT_PANELS };
  }
}

function savePanels(s: UiPanels) {
  try {
    const data: UiPanels = {
      characterPane: s.characterPane,
      spellBook: s.spellBook,
      talentTree: s.talentTree,
      achievements: s.achievements,
      questLog: s.questLog,
      social: s.social,
      pvp: s.pvp,
      lfg: s.lfg,
      help: s.help,
      shop: s.shop,
      bagsAll: s.bagsAll,
      mainMenu: s.mainMenu,
    };
    localStorage.setItem(PANELS_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota / private mode */
  }
}

export const useUiPanels = create<UiPanelsStore>((set, get) => ({
  ...loadPanels(),
  toggle: (key) => {
    set({ [key]: !get()[key] } as Partial<UiPanelsStore>);
    savePanels(get());
  },
  set: (key, value) => {
    set({ [key]: value } as Partial<UiPanelsStore>);
    savePanels(get());
  },
  closeAll: () => {
    set({ ...DEFAULT_PANELS });
    savePanels(get());
  },
}));

/* ─────────────────────────────────────────────────────────────────
 * Button definitions — order matches WoW 3.3.5a micro-menu row.
 * ──────────────────────────────────────────────────────────────── */

interface MicroBtn {
  id: string;
  panel: keyof UiPanels | null;
  label: string;
  glyph: ReactNode;
  hotkey: string;
  /** Basename of the original 3.3.5 micro-button art (Up/Down variants). */
  art?: string;
}

const BUTTONS: MicroBtn[] = [
  { id: 'character',    panel: 'characterPane', label: 'Персонаж',     glyph: '⚔', hotkey: 'C',  art: 'UI-MicroButtonCharacter' },
  { id: 'spellbook',    panel: 'spellBook',     label: 'Книга умений', glyph: '✶', hotkey: 'P',  art: 'UI-MicroButton-Spellbook' },
  { id: 'talents',      panel: 'talentTree',    label: 'Стезя',        glyph: '✦', hotkey: 'N',  art: 'UI-MicroButton-Talents' },
  { id: 'achievements', panel: 'achievements',  label: 'Достижения',   glyph: '✷', hotkey: 'Y',  art: 'UI-MicroButton-Achievement' },
  { id: 'questlog',     panel: 'questLog',      label: 'Журнал',       glyph: '❦', hotkey: 'L',  art: 'UI-MicroButton-Quest' },
  { id: 'social',       panel: 'social',        label: 'Социум',       glyph: '☉', hotkey: 'O',  art: 'UI-MicroButton-Socials' },
  { id: 'pvp',          panel: 'pvp',           label: 'PvP',          glyph: '⚖', hotkey: 'H',  art: 'UI-MicroButton-World' },
  { id: 'lfg',          panel: 'lfg',           label: 'Поиск группы', glyph: '☗', hotkey: 'I',  art: 'UI-MicroButton-LFG' },
  { id: 'help',         panel: 'help',          label: 'Помощь',       glyph: '?', hotkey: '?',  art: 'UI-MicroButton-Help' },
  { id: 'shop',         panel: 'shop',          label: 'Магазин',      glyph: '⚜', hotkey: '',   art: 'UI-MicroButton-BStore' },
  { id: 'bags',         panel: 'bagsAll',       label: 'Сумки',        glyph: '◈', hotkey: 'B',  art: 'Button-Backpack' },
  { id: 'menu',         panel: 'mainMenu',      label: 'Меню',         glyph: '☰', hotkey: 'Esc', art: 'UI-MicroButton-MainMenu' },
];

/** Root of the extracted 3.3.5 interface art on the hive. */
const UI_ART = `${import.meta.env.BASE_URL}ui-sirus/Interface/BUTTONS`;

/**
 * Button size & row gap. Both expressed as CSS strings so they can be
 * served from the rubber-UI token system (see styles/sirus-tokens.css).
 * BTN_SIZE clamps 22-36px via `--aiwari-slot-micro`; ROW_GAP clamps 2-6px.
 */
const BTN_SIZE = 'var(--aiwari-slot-micro)';
const ROW_GAP = 'var(--aiwari-gap)';

/* ─────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────── */

export function MicroMenu() {
  const stage = useUI((s) => s.stage);
  const panels = useUiPanels();
  const [hover, setHover] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  /* ── Keyboard shortcuts (only when stage === 'world') ── */
  useEffect(() => {
    if (stage !== 'world') return;
    const handler = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      const tag = tgt?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tgt?.isContentEditable) return;
      // Translate the keyboard event to an upper-case label our buttons use.
      const k = e.key.toUpperCase();
      const map: Record<string, keyof UiPanels> = {
        C: 'characterPane',
        P: 'spellBook',
        N: 'talentTree',
        Y: 'achievements',
        L: 'questLog',
        O: 'social',
        H: 'pvp',
        I: 'lfg',
        B: 'bagsAll',
      };
      if (e.key === 'Escape') {
        e.preventDefault();
        useUiPanels.getState().toggle('mainMenu');
        return;
      }
      const target = map[k];
      if (target) {
        e.preventDefault();
        useUiPanels.getState().toggle(target);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [stage]);

  const onClick = (b: MicroBtn) => {
    if (b.panel) {
      useUiPanels.getState().toggle(b.panel);
    }
    // Side-effect bridges for not-yet-built panels — keep tooltips/UX flowing.
    if (b.id === 'achievements' || b.id === 'social' || b.id === 'pvp' ||
        b.id === 'lfg' || b.id === 'help' || b.id === 'shop') {
      // eslint-disable-next-line no-console
      console.debug(`[sirus/MicroMenu] open: ${b.id}`);
    }
  };

  const isActive = (b: MicroBtn) => (b.panel ? !!panels[b.panel] : false);

  const buttonStyle = (active: boolean, hovered: boolean, art?: string): CSSProperties => art ? ({
    // Genuine 3.3.5 art. Micro buttons are 32×64 with the face on top;
    // the backpack is a square 64×64 icon-button.
    ...(art.startsWith('Button-')
      ? { width: `calc(${BTN_SIZE} * 1.15)`, height: `calc(${BTN_SIZE} * 1.15)`, backgroundSize: 'cover' }
      : { width: `calc(${BTN_SIZE} * 0.9)`, height: `calc(${BTN_SIZE} * 1.55)`, backgroundSize: '100% auto' }),
    padding: 0,
    background: 'transparent',
    // Backpack has no -Down art — press feedback via brightness instead.
    backgroundImage: `url("${UI_ART}/${art}-${active && !art.startsWith('Button-') ? 'Down' : 'Up'}.png")`,
    backgroundPosition: 'top center',
    backgroundRepeat: 'no-repeat',
    border: 'none',
    filter: active && art.startsWith('Button-') ? 'brightness(0.75)' : hovered && !active ? 'brightness(1.35)' : 'none',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'filter 100ms',
  }) : ({
    width: BTN_SIZE,
    height: BTN_SIZE,
    padding: 0,
    background: active
      ? 'radial-gradient(circle, #2A2014, #0B0907)'
      : hovered
        ? 'radial-gradient(circle, #1F1813, #0B0907)'
        : 'radial-gradient(circle, #1A140F, #0B0907)',
    border: `1px solid ${active ? 'var(--aiwari-gold-soft, #FFD479)' : hovered ? 'var(--aiwari-gold, #FAB300)' : 'var(--aiwari-frame-edge, #5C4520)'}`,
    boxShadow: active
      ? '0 0 8px rgba(255,212,121,0.5), inset 0 0 4px rgba(201,169,106,0.25)'
      : hovered
        ? '0 0 6px rgba(201,169,106,0.4)'
        : 'inset 0 0 3px rgba(0,0,0,0.6)',
    color: active ? '#FFD479' : hovered ? '#E8C77B' : 'var(--aiwari-fg-muted, #B19B7E)',
    fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
    fontSize: 'var(--aiwari-text-md)',
    lineHeight: 1,
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 120ms, border-color 120ms, box-shadow 120ms',
  });

  // Retail 3.3.5 placement: the micro-menu sits on the bottom strip to the
  // RIGHT of the main action bar (bags further right, in BagBar).
  const containerStyle: CSSProperties = {
    position: 'fixed',
    bottom: 10,
    left: 'calc(50% + var(--aiwari-slot) * 6.6 + 24px)',
    zIndex: 30,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 0,
    padding: 0,
    background: 'transparent',
    pointerEvents: 'auto',
  };

  const useMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(pointer: coarse) and (max-width: 640px)').matches;
  }, []);

  if (stage !== 'world') return null;

  /* ── Mobile hamburger column ── */
  if (useMobile) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 100,
          right: 12,
          zIndex: 30,
          pointerEvents: 'auto',
        }}
      >
        <button
          type="button"
          onClick={() => setMobileOpen((o) => !o)}
          style={{
            width: 44,
            height: 44,
            background: 'linear-gradient(180deg, #1F1813, #0B0907)',
            border: '2px solid var(--aiwari-gold, #C9A96A)',
            color: 'var(--aiwari-gold-soft, #FFD479)',
            fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
            fontSize: 22,
            cursor: 'pointer',
          }}
          aria-label="Меню"
        >
          ☰
        </button>
        {mobileOpen && (
          <div
            style={{
              position: 'absolute',
              bottom: 50,
              right: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, var(--aiwari-slot-micro))',
              gap: 'var(--aiwari-gap)',
              padding: 'var(--aiwari-padding)',
              background: 'linear-gradient(180deg, #161210, #0B0907)',
              border: '1px solid var(--aiwari-frame-edge, #5C4520)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.85)',
            }}
          >
            {BUTTONS.map((b) => {
              const active = isActive(b);
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => {
                    onClick(b);
                    setMobileOpen(false);
                  }}
                  title={`${b.label}${b.hotkey ? ` (${b.hotkey})` : ''}`}
                  style={{ ...buttonStyle(active, false), width: 'var(--aiwari-slot-micro)', height: 'var(--aiwari-slot-micro)', fontSize: 'var(--aiwari-text-lg)' }}
                >
                  {b.glyph}
                </button>
              );
            })}
          </div>
        )}
        {panels.mainMenu && <SimpleMenuModal />}
      </div>
    );
  }

  return (
    <>
      <div className="aiwari-micro-menu" style={containerStyle}>
        {BUTTONS.map((b) => {
          const active = isActive(b);
          const hovered = hover === b.id;
          return (
            <button
              key={b.id}
              type="button"
              className="aiwari-icon-slot aiwari-stone-btn"
              onMouseEnter={() => setHover(b.id)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onClick(b)}
              title={`${b.label}${b.hotkey ? ` (${b.hotkey})` : ''}`}
              style={buttonStyle(active, hovered, b.art)}
              aria-pressed={active}
            >
              {!b.art && <span aria-hidden style={{ pointerEvents: 'none' }}>{b.glyph}</span>}
            </button>
          );
        })}
      </div>
      {panels.mainMenu && <SimpleMenuModal />}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Placeholder modal for the «Меню» button until MainMenu is built.
 * ──────────────────────────────────────────────────────────────── */

function SimpleMenuModal() {
  const close = () => useUiPanels.getState().set('mainMenu', false);
  const openSettings = () => {
    close();
    useUiPanels.getState().set('help', false);
    // Settings opens via a separate event the SettingsPanel listens for.
    window.dispatchEvent(new CustomEvent('aiwari-open-settings'));
  };
  const logout = () => {
    close();
    window.dispatchEvent(new CustomEvent('aiwari-logout'));
  };

  return (
    <div
      role="dialog"
      aria-modal
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 49,
      }}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 320,
          padding: 20,
          background: 'var(--aiwari-bg-card-solid, #161210)',
          border: '1px solid var(--aiwari-frame-edge, #5C4520)',
          color: 'var(--aiwari-fg, #E8E1DA)',
          fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)', fontSize: 18, marginBottom: 16 }}>
          Главное меню
        </div>
        <button type="button" onClick={close} style={menuBtnStyle}>Продолжить</button>
        <button type="button" onClick={openSettings} style={menuBtnStyle}>Настройки</button>
        <button type="button" onClick={logout} style={{ ...menuBtnStyle, borderColor: '#5C2520', color: '#FF8A80' }}>
          Выход
        </button>
      </div>
    </div>
  );
}

const menuBtnStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  marginBottom: 8,
  background: '#1F1813',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-gold-soft, #FFD479)',
  fontFamily: 'inherit',
  fontSize: 14,
  cursor: 'pointer',
};

export default MicroMenu;
